import { useState } from 'react';
import {
  Alert as AntAlert,
  Button,
  Descriptions,
  Form,
  Input,
  Modal,
  Select,
  Tag,
  Typography,
} from 'antd';
import { DeleteOutlined, PlusOutlined, ThunderboltOutlined } from '@ant-design/icons';
import {
  ALERT_SEVERITIES,
  routeFailure,
  sendTestSignal,
  type TestSignalResult,
} from '@/api/alerts';
import { normalizeApiError } from '@/api/errors';

const { Text } = Typography;

interface FormShape {
  type: string;
  severity: string;
  fields: { key: string; value: string }[];
}

export interface TestSignalModalProps {
  open: boolean;
  onClose: () => void;
  /** Open the created alert on the register (the result panel's link). */
  onOpenAlert: (result: TestSignalResult) => void;
  /** Called once per successful fire so the register can refresh. */
  onFired: () => void;
}

function OutcomeTag({ outcome }: { outcome: string }) {
  switch (outcome) {
    case 'ROUTED':
      return <Tag color="green">ROUTED</Tag>;
    case 'SUPPRESSED':
      return <Tag>SUPPRESSED</Tag>;
    case 'FAILED':
      return <Tag color="red">ROUTING FAILED</Tag>;
    default:
      return <Tag color="gold">NO RULE MATCHED</Tag>;
  }
}

/**
 * Send a test signal (OPSFL-FIX-1) — the console-side proof that alert routing works, WITHOUT
 * the external HMAC rail: the synthetic signal goes through the REAL internal intake and the
 * REAL routing rules; the resulting alert is durable, on the register, and permanently marked
 * `signal_source = CONSOLE_TEST` so it can never masquerade as a real signal. The signal type
 * is gated by the hub's signal-type catalogue exactly like the machine rail (unknown → 422,
 * reserved → 409) — shown verbatim, never softened.
 */
export function TestSignalModal({ open, onClose, onOpenAlert, onFired }: TestSignalModalProps) {
  const [form] = Form.useForm<FormShape>();
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<TestSignalResult | null>(null);

  const fire = async () => {
    const values = await form.validateFields();
    setSubmitting(true);
    setSubmitError(null);
    const fields: Record<string, string> = {};
    for (const pair of values.fields ?? []) {
      if (pair?.key?.trim()) fields[pair.key.trim()] = pair.value ?? '';
    }
    try {
      const fired = await sendTestSignal({
        type: values.type.trim(),
        severity: values.severity,
        fields: Object.keys(fields).length ? fields : null,
      });
      setResult(fired);
      onFired();
    } catch (e) {
      // The API names exactly what it refused (unknown type 422, reserved 409, 503) — verbatim.
      setSubmitError(normalizeApiError(e).message);
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setSubmitError(null);
    setResult(null);
    form.resetFields();
  };

  const failure = result ? routeFailure(result.alert) : null;

  return (
    <Modal
      open={open}
      title={
        <span>
          <ThunderboltOutlined style={{ marginRight: 8 }} aria-hidden />
          Send test signal
        </span>
      }
      onCancel={() => {
        reset();
        onClose();
      }}
      footer={
        result ? (
          <>
            <Button onClick={reset}>Send another</Button>
            <Button
              type="primary"
              onClick={() => {
                const fired = result;
                reset();
                onClose();
                onOpenAlert(fired);
              }}
            >
              View alert on register
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={() => {
                reset();
                onClose();
              }}
            >
              Cancel
            </Button>
            <Button type="primary" loading={submitting} onClick={() => void fire()}>
              Fire test signal
            </Button>
          </>
        )
      }
      width={620}
      destroyOnClose
    >
      <Text type="secondary" style={{ fontSize: 12.5, display: 'block', marginBottom: 12 }}>
        Injects a synthetic signal through the <b>same</b> internal intake and routing path the
        machine rail uses — the type must exist in this hub&rsquo;s signal catalogue, rules run
        for real, and the resulting alert lands durably on the register, permanently marked{' '}
        <Text className="ol-mono" style={{ fontSize: 11 }}>
          signal_source = CONSOLE_TEST
        </Text>
        . A test can never masquerade as a real signal.
      </Text>

      {submitError && (
        <AntAlert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="The API refused this test signal"
          description={submitError}
        />
      )}

      {result ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {failure && (
            <AntAlert
              type="error"
              showIcon
              message="A rule matched but could not act"
              description={failure}
            />
          )}
          <Descriptions
            size="small"
            column={1}
            bordered
            items={[
              {
                key: 'outcome',
                label: 'Routing outcome',
                children: (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <OutcomeTag outcome={result.routingOutcome} />
                    {result.alert.routedRuleId && (
                      <Text className="ol-mono" style={{ fontSize: 11 }}>
                        rule {result.alert.routedRuleId}
                      </Text>
                    )}
                    {result.alert.routedRunId && (
                      <Text className="ol-mono" style={{ fontSize: 11 }}>
                        → run {result.alert.routedRunId}
                      </Text>
                    )}
                  </span>
                ),
              },
              {
                key: 'alert',
                label: 'Alert',
                children: (
                  <Text className="ol-mono" style={{ fontSize: 11.5 }} copyable>
                    {result.alert.alertId}
                  </Text>
                ),
              },
              {
                key: 'signal',
                label: 'Signal',
                children: (
                  <span style={{ fontSize: 12 }}>
                    <Text className="ol-mono" style={{ fontSize: 11.5 }}>
                      {result.signalId}
                    </Text>{' '}
                    <Tag style={{ marginInlineStart: 6 }}>{result.signalState}</Tag>
                    <Tag color="purple">CONSOLE_TEST</Tag>
                  </span>
                ),
              },
            ]}
          />
        </div>
      ) : (
        <Form<FormShape>
          form={form}
          layout="vertical"
          initialValues={{ severity: 'NORMAL', fields: [] }}
        >
          <div style={{ display: 'flex', gap: 12 }}>
            <Form.Item
              name="type"
              label="Signal type"
              style={{ flex: 1 }}
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: 'The hub catalogue gates the type (exactly like the real rail).',
                },
              ]}
            >
              <Input placeholder="e.g. CKYCRR_UPDATE_NOTIFICATION" maxLength={60} />
            </Form.Item>
            <Form.Item name="severity" label="Alert severity" style={{ width: 140 }}>
              <Select options={ALERT_SEVERITIES.map((s) => ({ value: s, label: s }))} />
            </Form.Item>
          </div>

          <Form.List name="fields">
            {(fields, { add, remove }) => (
              <div style={{ marginBottom: 4 }}>
                {fields.map((field) => (
                  <div key={field.key} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                    <Form.Item
                      name={[field.name, 'key']}
                      noStyle
                      rules={[{ required: true, message: 'Field name required.' }]}
                    >
                      <Input
                        placeholder="detail field (e.g. update_kind)"
                        style={{ flex: 1 }}
                        aria-label="Detail field name"
                      />
                    </Form.Item>
                    <Form.Item name={[field.name, 'value']} noStyle>
                      <Input placeholder="value" style={{ flex: 1 }} aria-label="Field value" />
                    </Form.Item>
                    <Button
                      icon={<DeleteOutlined />}
                      aria-label="Remove field"
                      onClick={() => remove(field.name)}
                    />
                  </div>
                ))}
                <Button
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={() => add({ key: '', value: '' })}
                >
                  Add flat field
                </Button>
                <div style={{ marginTop: 6 }}>
                  <Text type="secondary" style={{ fontSize: 11.5 }}>
                    Flat fields ride the signal&rsquo;s detail (like a real sender&rsquo;s would).
                    The alert itself carries only the three facts every SIGNAL alert has — rules
                    are exercised on exactly what production can produce.
                  </Text>
                </div>
              </div>
            )}
          </Form.List>
        </Form>
      )}
    </Modal>
  );
}
